import { HostMessage, HostMessageType } from './interfaces/host-message-types';
import { isNonNullObject } from 'utilities';
import { ExternalCommandPayload } from './interfaces/host-message-payloads.types';
import type { AddNewModelPayload } from './messages/add-new-model/interfaces/add-new-model-payload.interface';
import type { ModelDeletedPayload } from './messages/model-deleted/interfaces/model-deleted-payload.interface';
import type { ModelUploadedPayload } from './messages/model-upload/interfaces/model-uploaded-payload.interface';
import type { ViewerReadyPayload } from './messages/viewer-ready/interfaces/viewer-ready-payload.interface';
import type { ViewerErrorPayload } from './messages/viewer-error/interfaces/viewer-error-payload.interface';

/** Validation result with optional error message */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/** Creates a successful validation result */
export function validResult(): ValidationResult {
  return { valid: true };
}

/** Creates a failed validation result */
export function invalidResult(error: string): ValidationResult {
  return { valid: false, error };
}

/**
 * Validates that a value is a non-empty string.
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Validates that a value is a valid HostMessageType.
 */
export function isValidMessageType(type: unknown): type is HostMessageType {
  return Object.values(HostMessageType).includes(type as HostMessageType);
}

/**
 * Validates the structure of a HostMessage.
 */
export function validateHostMessage(message: unknown): ValidationResult {
  if (!isNonNullObject(message)) {
    return invalidResult('Message must be an object');
  }

  const msg = message as Record<string, unknown>;

  if (!('type' in msg)) {
    return invalidResult('Message must have a type property');
  }

  if (!isValidMessageType(msg['type'])) {
    return invalidResult(
      `Invalid message type: ${typeof msg['type'] === 'string' ? msg['type'] : 'unknown'}`
    );
  }

  return validResult();
}

/**
 * Validates AddNewModelPayload structure.
 */
export function validateAddNewModelPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  // teamId, projectId, name can be undefined but if present must be strings
  if (p['teamId'] !== undefined && typeof p['teamId'] !== 'string') {
    return invalidResult('teamId must be a string or undefined');
  }

  if (p['projectId'] !== undefined && typeof p['projectId'] !== 'string') {
    return invalidResult('projectId must be a string or undefined');
  }

  if (p['name'] !== undefined && typeof p['name'] !== 'string') {
    return invalidResult('name must be a string or undefined');
  }

  return validResult();
}

/**
 * Validates ModelUploadedPayload structure.
 */
export function validateModelUploadedPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['modelId'])) {
    return invalidResult('modelId is required and must be a non-empty string');
  }

  if (!isNonEmptyString(p['projectId'])) {
    return invalidResult('projectId is required and must be a non-empty string');
  }

  if (!isNonEmptyString(p['name'])) {
    return invalidResult('name is required and must be a non-empty string');
  }

  return validResult();
}

/**
 * Validates ModelDeletedPayload structure.
 */
export function validateModelDeletedPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['modelId'])) {
    return invalidResult('modelId is required and must be a non-empty string');
  }

  if (!isNonEmptyString(p['projectId'])) {
    return invalidResult('projectId is required and must be a non-empty string');
  }

  return validResult();
}

/**
 * Validates ViewerReadyPayload structure.
 */
export function validateViewerReadyPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['viewerId'])) {
    return invalidResult('viewerId is required and must be a non-empty string');
  }

  if (p['capabilities'] !== undefined && !Array.isArray(p['capabilities'])) {
    return invalidResult('capabilities must be an array if provided');
  }

  return validResult();
}

/**
 * Validates ViewerErrorPayload structure.
 */
export function validateViewerErrorPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['viewerId'])) {
    return invalidResult('viewerId is required and must be a non-empty string');
  }

  if (!isNonEmptyString(p['error'])) {
    return invalidResult('error is required and must be a non-empty string');
  }

  return validResult();
}

/**
 * Validates ExternalCommandPayload structure.
 */
export function validateExternalCommandPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['command'])) {
    return invalidResult('command is required and must be a non-empty string');
  }

  if (p['args'] !== undefined && !Array.isArray(p['args'])) {
    return invalidResult('args must be an array if provided');
  }

  return validResult();
}

/**
 * Validates TokenSetupPayload structure.
 */
export function validateTokenSetupPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['token'])) {
    return invalidResult('token is required and must be a non-empty string');
  }
  return validResult();
}

/**
 * Validates AuthenticatedPayload structure.
 */
export function validateAuthenticatedPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!isNonEmptyString(p['token'])) {
    return invalidResult('token is required and must be a non-empty string');
  }

  if (p['expiresAt'] !== undefined && typeof p['expiresAt'] !== 'number') {
    return invalidResult('expiresAt must be a number if provided');
  }

  return validResult();
}

/**
 * Validates UploadSetupPayload structure.
 */
export function validateUploadSetupPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (p['items'] !== undefined && !Array.isArray(p['items'])) {
    return invalidResult('items must be an array if provided');
  }

  return validResult();
}

/**
 * Validates SceneSetupPayload structure.
 */
export function validateSceneSetupPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  // Validate optional string fields
  if (p['id'] !== undefined && typeof p['id'] !== 'string') {
    return invalidResult('id must be a string');
  }
  if (p['name'] !== undefined && typeof p['name'] !== 'string') {
    return invalidResult('name must be a string');
  }
  if (p['parentId'] !== undefined && typeof p['parentId'] !== 'string') {
    return invalidResult('parentId must be a string');
  }
  if (p['objectId'] !== undefined && typeof p['objectId'] !== 'string') {
    return invalidResult('objectId must be a string');
  }
  if (p['thumbnail'] !== undefined && typeof p['thumbnail'] !== 'string') {
    return invalidResult('thumbnail must be a string');
  }
  if (p['viewMode'] !== undefined && typeof p['viewMode'] !== 'string') {
    return invalidResult('viewMode must be a string');
  }

  // Validate optional number field
  if (p['orderNumber'] !== undefined && typeof p['orderNumber'] !== 'number') {
    return invalidResult('orderNumber must be a number');
  }

  // Validate optional object fields
  if (p['camera'] !== undefined && !isNonNullObject(p['camera'])) {
    return invalidResult('camera must be an object');
  }
  if (p['objects'] !== undefined && !isNonNullObject(p['objects'])) {
    return invalidResult('objects must be an object');
  }
  if (p['viewBox'] !== undefined && !isNonNullObject(p['viewBox'])) {
    return invalidResult('viewBox must be an object');
  }
  if (p['author'] !== undefined && !isNonNullObject(p['author'])) {
    return invalidResult('author must be an object');
  }

  // Validate optional array fields
  if (p['topology'] !== undefined && !Array.isArray(p['topology'])) {
    return invalidResult('topology must be an array');
  }
  if (p['layers'] !== undefined && !Array.isArray(p['layers'])) {
    return invalidResult('layers must be an array');
  }
  if (p['attachments'] !== undefined && !Array.isArray(p['attachments'])) {
    return invalidResult('attachments must be an array');
  }
  if (p['overlays'] !== undefined && !Array.isArray(p['overlays'])) {
    return invalidResult('overlays must be an array');
  }
  if (p['clippingPlanes'] !== undefined && !Array.isArray(p['clippingPlanes'])) {
    return invalidResult('clippingPlanes must be an array');
  }

  return validResult();
}

/**
 * Validates OpenedPagePayload structure.
 */
export function validateOpenedPagePayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (typeof p['page'] !== 'string' || p['page'].trim() === '') {
    return invalidResult('page is required and must be a non-empty string');
  }

  return validResult();
}

const VALID_UPLOAD_STATUSES = new Set([
  'Preparing',
  'Processing',
  'Uploading',
  'Finalizing',
  'Uploaded',
  'Cancelled',
  'Removed',
]);

/**
 * Validates ModelUploadStatusPayload structure ({ items: [...] of status items })..
 */
export function validateModelUploadStatusPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;

  if (!Array.isArray(p['items'])) {
    return invalidResult('items must be an array');
  }

  for (const item of p['items'] as unknown[]) {
    if (!item || typeof item !== 'object') {
      return invalidResult('Each item must be an object');
    }
    const i = item as Record<string, unknown>;
    if (!isNonEmptyString(i['id'])) {
      return invalidResult('id is required and must be a non-empty string');
    }
    if (!isNonEmptyString(i['name'])) {
      return invalidResult('name is required and must be a non-empty string');
    }
    if (typeof i['status'] !== 'string' || !VALID_UPLOAD_STATUSES.has(i['status'])) {
      return invalidResult(
        'status must be one of: Preparing, Processing, Uploading, Finalizing, Uploaded, Cancelled, Removed'
      );
    }
    if (!isNonEmptyString(i['projectId'])) {
      return invalidResult('projectId is required and must be a non-empty string');
    }
    if (!isNonEmptyString(i['teamId'])) {
      return invalidResult('teamId is required and must be a non-empty string');
    }
    if (i['modelId'] !== undefined && !isNonEmptyString(i['modelId'])) {
      return invalidResult('modelId must be a non-empty string when present');
    }
    if (
      i['percentage'] !== undefined &&
      (typeof i['percentage'] !== 'number' ||
        !Number.isFinite(i['percentage']) ||
        i['percentage'] < 0 ||
        i['percentage'] > 100)
    ) {
      return invalidResult('percentage must be a finite number between 0 and 100 when present');
    }
  }

  return validResult();
}

/**
 * Validates CancelModelUploadPayload structure ({ items: CancelModelUploadItem[] }).
 */
export function validateCancelModelUploadPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;
  if (!Array.isArray(p['items'])) {
    return invalidResult('items must be an array');
  }

  for (const item of p['items'] as unknown[]) {
    if (!item || typeof item !== 'object') {
      return invalidResult('Each item must be an object');
    }
    const i = item as Record<string, unknown>;
    if (!isNonEmptyString(i['id'])) {
      return invalidResult('id is required and must be a non-empty string');
    }
    if (!isNonEmptyString(i['projectId'])) {
      return invalidResult('projectId is required and must be a non-empty string');
    }
    if (!isNonEmptyString(i['teamId'])) {
      return invalidResult('teamId is required and must be a non-empty string');
    }
  }

  return validResult();
}

/**
 * Validates CancelModelSetupPayload structure ({ projectId: string, teamId: string }).
 */
export function validateCancelModelSetupPayload(payload: unknown): ValidationResult {
  if (!isNonNullObject(payload)) {
    return invalidResult('Payload must be an object');
  }

  const p = payload as Record<string, unknown>;
  if (!isNonEmptyString(p['projectId'])) {
    return invalidResult('projectId is required and must be a non-empty string');
  }
  if (!isNonEmptyString(p['teamId'])) {
    return invalidResult('teamId is required and must be a non-empty string');
  }

  return validResult();
}

/**
 * Gets the appropriate validator for a message type.
 */
export function getValidatorForType(
  type: HostMessageType
): ((payload: unknown) => ValidationResult) | null {
  const validators: Partial<Record<HostMessageType, (payload: unknown) => ValidationResult>> = {
    [HostMessageType.AddNewModel]: validateAddNewModelPayload,
    [HostMessageType.ModelUploaded]: validateModelUploadedPayload,
    [HostMessageType.ModelDeleted]: validateModelDeletedPayload,
    [HostMessageType.ViewerReady]: validateViewerReadyPayload,
    [HostMessageType.ViewerError]: validateViewerErrorPayload,
    [HostMessageType.ExternalCommand]: validateExternalCommandPayload,
    [HostMessageType.TokenSetup]: validateTokenSetupPayload,
    [HostMessageType.Authenticated]: validateAuthenticatedPayload,
    [HostMessageType.UploadSetup]: validateUploadSetupPayload,
    [HostMessageType.SceneSetup]: validateSceneSetupPayload,
    [HostMessageType.OpenedPage]: validateOpenedPagePayload,
    [HostMessageType.ModelUploadStatus]: validateModelUploadStatusPayload,
    [HostMessageType.CancelModelUpload]: validateCancelModelUploadPayload,
    [HostMessageType.CancelModelSetup]: validateCancelModelSetupPayload,
  };

  return validators[type] ?? null;
}

/**
 * Validates a complete host message including its payload.
 */
export function validateHostMessageWithPayload(message: unknown): ValidationResult {
  const messageValidation = validateHostMessage(message);
  if (!messageValidation.valid) {
    return messageValidation;
  }

  const msg = message as HostMessage;
  const validator = getValidatorForType(msg.type);

  if (validator && msg.payload !== undefined) {
    return validator(msg.payload);
  }

  return validResult();
}

/** Type guards for specific payload types */
export function isAddNewModelPayload(payload: unknown): payload is AddNewModelPayload {
  return validateAddNewModelPayload(payload).valid;
}

export function isModelUploadedPayload(payload: unknown): payload is ModelUploadedPayload {
  return validateModelUploadedPayload(payload).valid;
}

export function isModelDeletedPayload(payload: unknown): payload is ModelDeletedPayload {
  return validateModelDeletedPayload(payload).valid;
}

export function isViewerReadyPayload(payload: unknown): payload is ViewerReadyPayload {
  return validateViewerReadyPayload(payload).valid;
}

export function isViewerErrorPayload(payload: unknown): payload is ViewerErrorPayload {
  return validateViewerErrorPayload(payload).valid;
}

export function isExternalCommandPayload(payload: unknown): payload is ExternalCommandPayload {
  return validateExternalCommandPayload(payload).valid;
}
